import React, { useState, useEffect } from 'react';
import { getParametrosEfetivos, updateParametros } from '../api';

const fmt  = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmt4 = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4 });

function Campo({ label, hint, value, onChange, min, max, step, prefix, suffix }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      {hint && <div style={{ fontSize: 11, color: 'var(--c4)', marginBottom: 4 }}>{hint}</div>}
      <div className="inp-r" style={{ maxWidth: 260 }}>
        {prefix && <span style={{ padding: '0 10px', fontSize: 13, color: 'var(--c4)', flexShrink: 0 }}>{prefix}</span>}
        <input className="inp" type="number" min={min ?? 0} max={max} step={step ?? 1}
          value={value} onChange={e => onChange(e.target.value)} />
        {suffix && <span style={{ padding: '0 10px', fontSize: 13, color: 'var(--c4)', flexShrink: 0 }}>{suffix}</span>}
      </div>
    </div>
  );
}

function InfoBox({ title, children, color }) {
  return (
    <div style={{ padding: '10px 14px', background: color || 'var(--g4)', borderRadius: 10, fontSize: 12, marginBottom: 8 }}>
      {title && <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>{title}</div>}
      {children}
    </div>
  );
}

export default function PrecificacaoForm({ onToast }) {
  const [efetivos, setEfetivos] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    salario_mensal: '0',
    dias_trabalho_mes: '20',
    horas_trabalho_dia: '5',
    percentual_horas_produtivas: '100',
    despesas_fixas_mensais: '0',
    fonte_despesas_fixas: 'manual',
    percentual_variaveis: '10',
    percentual_variaveis_venda: '0',
    percentual_markup: '70',
  });

  const load = async () => {
    try {
      const data = await getParametrosEfetivos();
      setEfetivos(data);
      const p = data.params;
      setForm({
        salario_mensal: String(Number(p.salario_mensal).toFixed(2)),
        dias_trabalho_mes: String(p.dias_trabalho_mes),
        horas_trabalho_dia: String(p.horas_trabalho_dia),
        percentual_horas_produtivas: String(Number(p.percentual_horas_produtivas || 100)),
        despesas_fixas_mensais: String(Number(p.despesas_fixas_mensais).toFixed(2)),
        fonte_despesas_fixas: p.fonte_despesas_fixas || 'manual',
        percentual_variaveis: String(Number(p.percentual_variaveis).toFixed(1)),
        percentual_variaveis_venda: String(Number(p.percentual_variaveis_venda || 0).toFixed(1)),
        percentual_markup: String(Number(p.percentual_markup).toFixed(1)),
      });
    } catch (err) { onToast(err.message, 'error'); }
  };

  useEffect(() => { load(); }, []);

  const set = k => v => setForm(prev => ({ ...prev, [k]: v }));

  // Pré-visualização calculada em tempo real
  const horasMes   = Number(form.dias_trabalho_mes) * Number(form.horas_trabalho_dia);
  const pctProd    = Number(form.percentual_horas_produtivas) / 100;
  const minProd    = horasMes * 60 * pctProd;
  const salario    = Number(form.salario_mensal);
  const despFixas  = form.fonte_despesas_fixas === 'previsto'
    ? (efetivos?.calculado?.despesasFixas || 0)
    : Number(form.despesas_fixas_mensais);
  const custoMinMO    = minProd > 0 ? salario / minProd : 0;
  const custoMinFixas = minProd > 0 ? despFixas / minProd : 0;
  const custoHoraMO   = custoMinMO * 60;
  const custoHoraFixas = custoMinFixas * 60;

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateParametros({
        salario_mensal:              Number(form.salario_mensal),
        dias_trabalho_mes:           Number(form.dias_trabalho_mes),
        horas_trabalho_dia:          Number(form.horas_trabalho_dia),
        percentual_horas_produtivas: Number(form.percentual_horas_produtivas),
        despesas_fixas_mensais:      Number(form.despesas_fixas_mensais),
        fonte_despesas_fixas:        form.fonte_despesas_fixas,
        percentual_variaveis:        Number(form.percentual_variaveis),
        percentual_variaveis_venda:  Number(form.percentual_variaveis_venda),
        percentual_markup:           Number(form.percentual_markup),
      });
      onToast('Parâmetros salvos! Receitas sendo recalculadas...');
      load();
    } catch (err) { onToast(err.message, 'error'); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>

      {/* ── Bloco 1: Seu tempo ─────────────────────────────────────────── */}
      <div className="c-t" style={{ marginBottom: 12 }}>⏱ Seu tempo</div>
      <Campo label="Dias trabalhados por mês" value={form.dias_trabalho_mes} onChange={set('dias_trabalho_mes')} min={1} max={31} />
      <Campo label="Horas por dia" value={form.horas_trabalho_dia} onChange={set('horas_trabalho_dia')} min={1} max={24} />
      <Campo
        label="% de horas produtivas"
        hint="Das horas que você trabalha, quanto vai para produção (não compra, limpeza, atendimento)?"
        value={form.percentual_horas_produtivas} onChange={set('percentual_horas_produtivas')}
        min={1} max={100} suffix="%"
      />
      <InfoBox>
        <div style={{ color: 'var(--c4)' }}>
          Total: <strong>{horasMes} h/mês</strong> · Produção: <strong>{(minProd / 60).toFixed(1)} h/mês ({minProd.toFixed(0)} min)</strong>
        </div>
      </InfoBox>

      {/* ── Bloco 2: Custo do seu minuto ───────────────────────────────── */}
      <div className="c-t" style={{ marginBottom: 12, marginTop: 20 }}>💰 Custo do seu minuto</div>

      <Campo label="Pró-labore mensal" prefix="R$" step="0.01" value={form.salario_mensal} onChange={set('salario_mensal')} />

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
          Fonte das despesas fixas
        </label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {[
            { v: 'previsto', l: '📅 Contas fixas cadastradas' },
            { v: 'manual',   l: '✏️ Valor manual' },
          ].map(opt => (
            <button
              key={opt.v} type="button"
              className={`btn btn-sm ${form.fonte_despesas_fixas === opt.v ? 'btn1' : 'btn3'}`}
              onClick={() => set('fonte_despesas_fixas')(opt.v)}
            >
              {opt.l}
            </button>
          ))}
        </div>
        {form.fonte_despesas_fixas === 'previsto' && efetivos?.calculado && (
          <InfoBox color="var(--g4)">
            <span style={{ color: 'var(--c4)' }}>
              {efetivos.calculado.fonteDescricao}: <strong>{fmt(efetivos.calculado.despesasFixas)}/mês</strong>
            </span>
            {efetivos.contasFixasLista?.length > 0 && (
              <ul style={{ margin: '6px 0 0 16px', fontSize: 11, color: 'var(--c4)' }}>
                {efetivos.contasFixasLista.map((c, i) => (
                  <li key={i}>{c.nome}: {fmt(c.valor_efetivo)}</li>
                ))}
              </ul>
            )}
          </InfoBox>
        )}
        {form.fonte_despesas_fixas === 'manual' && (
          <Campo
            label="Total de despesas fixas mensais"
            hint="Aluguel, luz, água, internet, MEI, contador, etc."
            prefix="R$" step="0.01"
            value={form.despesas_fixas_mensais} onChange={set('despesas_fixas_mensais')}
          />
        )}
      </div>

      <InfoBox>
        <div style={{ color: 'var(--c4)', lineHeight: 1.8 }}>
          Mão de obra: <strong>{fmt4(custoMinMO)}/min</strong> = <strong>{fmt(custoHoraMO)}/h</strong><br />
          Despesas fixas: <strong>{fmt4(custoMinFixas)}/min</strong> = <strong>{fmt(custoHoraFixas)}/h</strong><br />
          Em 30 min de receita: <strong>{fmt((custoMinMO + custoMinFixas) * 30)}</strong>
        </div>
      </InfoBox>

      {/* ── Bloco 3: Margens ───────────────────────────────────────────── */}
      <div className="c-t" style={{ marginBottom: 12, marginTop: 20 }}>📊 Margens</div>
      <Campo
        label="% Despesas variáveis (sobre o custo dos ingredientes)"
        hint="Gás de botijão, material de limpeza, perdas. Não inclua embalagens (são insumos)."
        value={form.percentual_variaveis} onChange={set('percentual_variaveis')} step="0.1" suffix="%"
      />
      <Campo
        label="% Taxas sobre venda"
        hint="Taxa de cartão, comissão de app de entrega. Reduz o preço sugerido."
        value={form.percentual_variaveis_venda} onChange={set('percentual_variaveis_venda')} step="0.1" suffix="%"
      />
      <Campo
        label="% Markup (lucro desejado)"
        hint="Aplicado sobre o custo total para chegar ao preço sugerido."
        value={form.percentual_markup} onChange={set('percentual_markup')} step="0.1" suffix="%"
      />

      <div style={{ padding: '10px 14px', background: 'var(--y3,#fefce8)', border: '1px solid var(--y,#ca8a04)', borderRadius: 10, fontSize: 12, color: 'var(--c2)', marginBottom: 16 }}>
        ⚠️ Salvar cria uma nova vigência a partir de hoje e recalcula o custo de <strong>todas</strong> as receitas. O histórico de meses anteriores não é alterado.
      </div>

      <button className="btn btn1 btn-lg" type="submit" disabled={saving}>
        {saving ? 'Salvando e recalculando...' : '💾 Salvar parâmetros'}
      </button>
    </form>
  );
}
